import * as Tooltip from '@radix-ui/react-tooltip';
import type { ChartExplanation } from '@dma/shared';

/**
 * An accessible "how is this calculated?" info icon for chart cards. Radix gives us
 * correct keyboard focus, Escape-to-dismiss and screen-reader semantics (SPEC § WCAG
 * 2.1 AA). The copy comes from @dma/shared CHART_HELP so it matches the Excel
 * methodology block exactly. Kept separate from the Recharts data-hover <Tooltip>.
 */
export function InfoTooltip({ help }: { help: ChartExplanation }) {
  return (
    <Tooltip.Provider delayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            aria-label="How this is calculated"
            className="inline-flex h-5 w-5 items-center justify-center rounded-full text-ink-soft transition hover:bg-black/5 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
          >
            <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden>
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.4" />
              <path d="M10 9v4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              <circle cx="10" cy="6.4" r="0.95" fill="currentColor" />
            </svg>
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side="top"
            align="end"
            sideOffset={6}
            collisionPadding={12}
            className="z-50 max-w-xs rounded-xl border border-black/10 bg-white p-3 text-sm text-ink shadow-lg"
          >
            <p className="font-medium">{help.what}</p>
            <p className="mt-1.5 text-ink-soft">{help.how}</p>
            <Tooltip.Arrow className="fill-white" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
